<template>
  <div>
    <div class="row align-items-center py-3">
      <div class="col-auto">
        <h1 class="d-inline-block">
          Parts
        </h1>
      </div>

      <div class="col-sm-auto ms-auto col-12 text-center">
        <button
          class="btn btn-primary-green w-100 mx-md-0 mx-auto add-btn"
          style="min-width: 200px; max-width: 200px"
          @click="newModal.show()"
        >
          <i class="fas fa-fw fa-1x fa-plus" />
          &nbsp;New Part
        </button>
        <button
          class="btn btn-outline-secondary ms-1"
          type="submit"
          @click="exportParts"
        >
          <i class="far fa-download" />
          &nbsp;Export
        </button>
      </div>
    </div>

    <part-search-form :query="query" />

    <template v-if="isLoading">
      <loading-animation text="Loading Parts..." />
    </template>

    <div v-else-if="!parts">
      <failed-to-load text="Failed to load parts" />
    </div>

    <template v-else-if="!parts.length">
      <no-results text="There are no parts" />
    </template>

    <template v-else>
      <div class="row">
        <div class="col-12">
          <div class="table-responsive">
            <table
              class="table table-hover mg-table"
              style="min-width: 250px"
            >
              <thead>
                <tr>
                  <th scope="col">
                    Part Number
                  </th>
                  <th scope="col">
                    Client
                  </th>
                  <th scope="col" />
                </tr>
              </thead>

              <tbody>
                <template v-for="part in parts">
                  <tr :key="part.id">
                    <td>{{ part.partNumber }}</td>
                    <td>{{ part.name }}</td>

                    <td class="text-end">
                      <button
                        class="btn btn-sm btn-outline-secondary mx-1"
                        @click="toggleEdit(part)"
                      >
                        <i class="fas fa-fw fa-1x fa-pen" />
                      </button>

                      <button
                        class="btn btn-sm btn-outline-warning"
                        @click="entityToArchiveId = part.id; archiveModal.show()"
                      >
                        <i class="fas fa-archive fa-fw fa-1x mx-1" />
                      </button>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <pagination
          v-show="totalCount > 0"
          :page="page"
          :total-count="totalCount"
          :items-per-page="itemsPerPage"
        />
      </div>
    </template>

    <archive-part-modal
      ref="archiveModal"
      :entity-id="entityToArchiveId"
    />

    <new-part-modal ref="newModal" />

    <edit-part-modal
      ref="editModal"
      :part="entityToEdit"
    />
  </div>
</template>

<script>
import { PartsAPI } from '../../api/modules';
import { PartSearchForm } from '../forms/search';
import {
  Pagination,
  LoadingAnimation,
  FailedToLoad
} from '../shared';
import { Modal } from '../../main';
import { NewPartModal } from '../shared/modals/new';
import { NoResults } from '../shared';
import { EditPartModal } from '../shared/modals/edit';
import { ArchivePartModal } from '../shared/modals/archive';

export default {
  name: 'PartPage',
  components: {
    NewPartModal,
    EditPartModal,
    NoResults,
    PartSearchForm,
    FailedToLoad,
    Pagination,
    LoadingAnimation,
    ArchivePartModal
  },
  data() {
    return {
      parts: null,
      isLoading: true,
      entityToEdit: null,
      page: 1,
      itemsPerPage: 20,
      totalCount: 0,
      query: {},
      searchForm: PartSearchForm,
      archiveModal: null,
      newModal: null,
      editModal: null,
      entityToArchiveId: null
    };
  },
  async created() {
    this.$emitter.on('modal.hide', this.modalHideHandler);
    this.$emitter.on('modal.archive', this.modalArchiveHandler);
    this.$emitter.on('part.add', this.partCreated);
    this.$emitter.on('part.edited', this.partEdited);
    this.$emitter.on('pagination.pageChanged', this.pageChanged);
    this.$emitter.on('form.search', this.formSearch);

    await this.loadParts();

    this.isLoading = false;
  },
  mounted() {
    this.archiveModal = new Modal(this.$refs.archiveModal.$el);
    this.newModal = new Modal(this.$refs.newModal.$el);
    this.editModal = new Modal(this.$refs.editModal.$el);

    this.$refs.editModal.$el.addEventListener('hidden.bs.modal', () => {
      this.entityToEdit = null;

      this.$emitter.emit('modal.hidden', 'partPage');
    });
    this.$refs.newModal.$el.addEventListener('hidden.bs.modal', () => {
      this.$emitter.emit('modal.hidden', 'partPage');
    });
  },
  unmounted() {
    this.$emitter.off('modal.hide', this.modalHideHandler);
    this.$emitter.off('modal.archive', this.modalArchiveHandler);
    this.$emitter.off('part.add', this.partCreated);
    this.$emitter.off('part.edited', this.partEdited);
    this.$emitter.off('pagination.pageChanged', this.pageChanged);
    this.$emitter.off('form.search', this.formSearch);
  },
  methods: {
    async loadParts() {
      this.isLoading = true;

      try {
        const response = await PartsAPI.getParts(this.page, this.itemsPerPage, this.query);

        this.parts = response.data;
        this.totalCount = response.summary.countAll;
      } catch {}

      this.isLoading = false;
    },
    async deletePart(id) {
      try {
        const response = await PartsAPI.deletePart(id);

        if (response) {
          await this.$alert.success('Part archived');
        }

        const index = this.parts.findIndex(part => part.id === id);

        this.parts.splice(index, 1);

      } catch {}
    },
    toggleEdit(part) {
      this.entityToEdit = part;

      this.editModal.show();
    },
    partCreated() {
      this.newModal.hide();
      this.loadParts();
    },
    partEdited(part) {
      const index = this.parts.findIndex(v => v.id === part.id);

      if (index !== -1) {
        this.$set(this.parts, index, part);
      }

      this.editModal.hide();
    },
    async pageChanged(page) {
      this.page = page;

      await this.loadParts();
    },
    async formSearch(values) {
      this.query = values;

      await this.loadParts();
    },
    modalHideHandler(event) {
      if (event.from === 'archivePartModal') {
        this.archiveModal.hide();
      }
    },
    async modalArchiveHandler(event) {
      if (event.from === 'archivePartModal') {
        await this.deletePart(event.id);

        if (this.page > 1 && this.parts.length === 1) {
          this.page--;
        }

        await this.loadParts();
      }
    },
    async exportParts() {
      const response = await PartsAPI.exportParts(this.query);

      if (response) {
        const link = document.createElement('a');
        link.href = `data:application/vnd.ms-excel;base64,${response}`;
        link.download = 'Parts.xls';
        link.click();
      }
    }
  }
};
</script>
